import React, { useState } from 'react';
import { Modal, Button, useToast } from '@economic/gaudi';
import firebase from 'firebase/app';

import internalApiClient from '../../../util/api/internalApiClient';
import { useLocalization, t, tBB } from '../../l10n/l10n';
import RequestRemoteAccessLogo from './request-access-logo.png';

import './style.css';
import { log, useCurrentAgreement, User, useRouteAuthorisation, useCurrentUser } from '@economic/react-app';

enum timeFrameText {
    hour = 'hour',
    day = 'day',
    week = 'week',
    month = 'month',
}

const getTimeFrameText = (timeFrame: timeFrameText): string => {
    switch (timeFrame) {
        case timeFrameText.hour:
            return t`TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_ANHOUR`;
        case timeFrameText.day:
            return t`TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_ADAY`;
        case timeFrameText.week:
            return t`TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_AWEEK`;
        case timeFrameText.month:
            return t`TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_AMONTH`;
    }
};

const showAccessPopup = (snapshotData: any, user: User, hasAccessAllSettings: boolean): boolean => {
    const currentDate = new Date().getTime() / 1000;
    const expireDate = snapshotData?.requestExpire.seconds;

    if (snapshotData && !snapshotData.responded && currentDate <= expireDate && hasAccessAllSettings && !user.isAdministrator) {
        return true;
    }

    return false;
};

const RequestRemoteAccessDialog: React.FC = () => {
    const [visible, setVisible] = React.useState<boolean>(false);
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();
    const [accessRequest, setAccessRequest] = useState<any>({});
    const textsLoading = useLocalization(textIds);
    const toast = useToast();
    const [hasAccessAllSettings] = useRouteAuthorisation('/settings');

    React.useEffect(() => {
        const db = firebase.firestore();
        const documentRef = db.collection('requestAccess').doc(String(agreement.number));

        documentRef
            .get()
            .then(snapshot => {
                const snapshotData = snapshot.data();
                setAccessRequest(snapshotData);

                setVisible(showAccessPopup(snapshotData, user, hasAccessAllSettings));
            })
            .catch(error => {
                log.error(error);
            });

        const unsubscribe = documentRef.onSnapshot(function(doc) {
            const snapshotData = doc.data();
            setAccessRequest(snapshotData);

            setVisible(showAccessPopup(snapshotData, user, hasAccessAllSettings));
        });

        return () => {
            unsubscribe();
        };
    }, []);

    if (!visible || textsLoading) return null;

    const showAccessGrantedNotification = () => {
        toast.success(t('TOAST_REQUEST_ACCESS_REQUEST_SUCCESS', { TIMEFRAME: getTimeFrameText(accessRequest.timeFrame) }));
    };

    const showAccessGrantedNotificationError = () => {
        toast.error(t`TOAST_REQUEST_ACCESS_REQUEST_ERROR`);
    };

    const showAccessDeniedNotification = () => {
        toast.information(
            t('TOAST_REQUEST_ACCESS_REQUEST_DENIED', {
                SUPPORTERNAME: accessRequest.supporterAgreementName,
            })
        );
    };

    const setRespondedFiresore = () => {
        const db = firebase.firestore();
        const documentRef = db.collection('requestAccess').doc(String(agreement.number));
        documentRef
            .get()
            .then(snapshot => {
                documentRef.set({ responded: true }, { merge: true }).catch(error => {
                    log.error(error);
                });
            })
            .catch(error => {
                log.error(error);
            });
    };

    const handleGrantAccess = () => {
        internalApiClient
            .post(`/agreementselector/addtimelimitedaccess`, {
                adminAgreementNumber: accessRequest.supporterAgreementNumber,
                adminUserId: accessRequest.userId,
                expires: accessRequest.timeFrame,
            })
            .then(r => {
                setRespondedFiresore();
                showAccessGrantedNotification();
                setVisible(false);
            })
            .catch(error => {
                if (error && error.response && error.response.status >= 400) {
                    showAccessGrantedNotificationError();
                    log.error(error);
                }
            });
    };

    const handleHide = () => {
        setVisible(false);
        showAccessDeniedNotification();
        setRespondedFiresore();
    };

    return (
        <Modal
            className="modal__container--requestaccess"
            visible={visible}
            hideOnClickOutside={false}
            hideOnEsc={false}
            onHide={handleHide}>
            <>
                <Modal.Header className="modal__header--request-access mb-6">
                    <img className="m-auto" src={RequestRemoteAccessLogo} alt="request access logo" />
                    <span className="block mt-3">{t`TEMPORARYACCESSREQUEST_POPUP_TITLE`}</span>
                </Modal.Header>
                <Modal.Content className="mt-6 leading-4 text-center">
                    <p>
                        {tBB('TEMPORARYACCESSREQUEST_POPUP_TEXT', {
                            SUPPORTERNAME: accessRequest.supporterAgreementName,
                            TIMEFRAME: getTimeFrameText(accessRequest.timeFrame),
                        })}
                    </p>
                    {!!accessRequest?.message ? (
                        <p>
                            {t`TEMPORARYACCESSREQUEST_POPUP_SUPPORTERMESSAGE`}
                            <p className="break-words">
                                <i>
                                    {accessRequest.message.split('\n').map(line => (
                                        <>
                                            {line}
                                            <br />
                                        </>
                                    ))}
                                </i>
                            </p>
                        </p>
                    ) : null}
                </Modal.Content>
                <Modal.Footer className="justify-center">
                    <Button appearance="primary" onClick={handleGrantAccess}>
                        {t`TEMPORARYACCESSREQUEST_POPUP_ALLOW_BUTTON`}
                    </Button>
                </Modal.Footer>
            </>
        </Modal>
    );
};

const textIds = [
    'TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_ANHOUR',
    'TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_ADAY',
    'TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_AWEEK',
    'TEMPORARYACCESSREQUEST_ACCESSGRANTEDTIMEFRAME_AMONTH',

    'TOAST_REQUEST_ACCESS_REQUEST_SUCCESS',
    'TOAST_REQUEST_ACCESS_REQUEST_DENIED',
    'TOAST_REQUEST_ACCESS_REQUEST_ERROR',

    'TEMPORARYACCESSREQUEST_POPUP_TITLE',
    'TEMPORARYACCESSREQUEST_POPUP_TEXT',
    'TEMPORARYACCESSREQUEST_POPUP_ALLOW_BUTTON',
    'TEMPORARYACCESSREQUEST_POPUP_SUPPORTERMESSAGE',
];

export default RequestRemoteAccessDialog;
